import {
    FormDefinition, FormFieldDef, FormFieldOption, FormFieldType, publishedOf,
} from "../../documents/sections/formSection/formSchema"

/** Codes and keys end up inside docx placeholders, so they stay plain identifiers. */
export const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/
export const KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/
export const OPTION_VALUE_PATTERN = /^[A-Za-z0-9_-]+$/

export const hasOptions = (type: FormFieldType) => type === 'select' || type === 'multiselect'

/** What a docx tag prints (the API's FormTag grammar). */
export type PlaceholderKind = 'value' | 'check' | 'option' | 'position'

export interface FieldPlaceholder {
    tag: string
    kind: PlaceholderKind
    /** For an `option` tag: the option it ticks. */
    option?: FormFieldOption
}

/**
 * Every docx tag a field offers: its value, a ☒/☐ for a checkbox, one ☒/☐ per option of a choice
 * field, and a person's position. Headings print nothing.
 */
export const placeholdersOf = (code: string, field: FormFieldDef): FieldPlaceholder[] => {
    if (field.type === 'heading') return []
    const base = `form.${code}.${field.key}`
    const tags: FieldPlaceholder[] = [{ tag: `{{${base}}}`, kind: 'value' }]
    if (field.type === 'checkbox') tags.push({ tag: `{{${base}?}}`, kind: 'check' })
    if (field.type === 'user') tags.push({ tag: `{{${base}.position}}`, kind: 'position' })
    if (hasOptions(field.type)) {
        for (const option of field.options ?? []) {
            if (option.value !== '') tags.push({ tag: `{{${base}=${option.value}?}}`, kind: 'option', option })
        }
    }
    return tags
}

export const emptyDefinition = (): FormDefinition => ({
    id: crypto.randomUUID(),
    // The server assigns the company (from the token) and a new id on create.
    companyId: 0,
    code: '',
    name: '',
    status: 'DRAFT',
    version: 0,
    fields: [],
})

/** "Data instalacji" → "dataInstalacji". Diacritics are dropped, since keys are ASCII. */
export const keyFromLabel = (label: string): string => {
    const words = label
        .replace(/[łŁ]/g, m => (m === 'ł' ? 'l' : 'L'))
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .split(/[^A-Za-z0-9]+/)
        .filter(w => w !== '')
    const key = words
        .map((w, i) => i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase())
        .join('')
    return /^[a-zA-Z]/.test(key) ? key : `field${key}`
}

/** `base`, else `base2`, `base3`… whichever is not taken. */
export const uniqueKey = (base: string, taken: Iterable<string>): string => {
    const set = new Set(taken)
    if (!set.has(base)) return base
    let n = 2
    while (set.has(`${base}${n}`)) n++
    return `${base}${n}`
}

/** Keys a new field must avoid: the current ones and every key ever published. */
export const takenKeys = (draft: FormDefinition, stored: FormDefinition | undefined, exceptFieldId?: string) => [
    ...draft.fields.filter(f => f.id !== exceptFieldId).map(f => f.key),
    ...publishedOf(stored).keys,
]

export const newField = (type: FormFieldType, taken: Iterable<string>): FormFieldDef => ({
    id: crypto.randomUUID(),
    key: uniqueKey(type === 'heading' ? 'heading' : 'field', taken),
    type,
    label: { PL: '', EN: '' },
    ...(type !== 'heading' && { width: 12 as const }),
    ...(hasOptions(type) && { options: [{ value: 'OPTION_1', label: { PL: '', EN: '' } }] }),
})

/** Keeps the field consistent with its new type: options only on selects, no input props on a heading. */
export const changeFieldType = (field: FormFieldDef, type: FormFieldType): FormFieldDef => {
    const { options, required, width, ...rest } = field
    return {
        ...rest,
        type,
        ...(type !== 'heading' && { required, width: width ?? 12 }),
        ...(hasOptions(type) && { options: options ?? [{ value: 'OPTION_1', label: { PL: '', EN: '' } }] }),
    }
}

export interface FieldLocks {
    /** Key and type: the field was published, so templates and stored values may point at it. */
    locked: boolean
    lockedOptionValues: Set<string>
}

/**
 * Locks follow the saved definition, not the draft: a field counts as published when the saved copy
 * has a field with the same id whose key was published.
 */
export const fieldLocks = (stored: FormDefinition | undefined, field: FormFieldDef): FieldLocks => {
    const original = stored?.fields.find(f => f.id === field.id)
    const published = publishedOf(stored)
    if (!original || !published.keys.includes(original.key)) return { locked: false, lockedOptionValues: new Set() }
    const reserved = published.options[original.key] ?? []
    return {
        locked: true,
        lockedOptionValues: new Set((original.options ?? []).map(o => o.value).filter(v => reserved.includes(v))),
    }
}

/**
 * Options have no id. New ones are only ever appended, so the first option holding a locked value
 * is the original, and a later duplicate stays editable (and is flagged as a duplicate).
 */
export const isOptionLocked = (locks: FieldLocks, options: FormFieldOption[], index: number) => {
    const value = options[index].value
    return locks.lockedOptionValues.has(value) && options.findIndex(o => o.value === value) === index
}

export type IssueTarget = 'name' | 'code' | 'fields' | 'key' | 'label' | 'options'

export interface DefinitionIssue {
    severity: 'error' | 'warning'
    target: IssueTarget
    fieldId?: string
    /** l10n id, with `vars` and a Polish fallback. */
    id: string
    vars?: Record<string, string>
    fallback: string
}

/**
 * Errors block saving and publishing. Warnings only matter at publish time: a missing translation
 * is shown, and the admin may publish anyway.
 */
export const validateDefinition = (
    draft: FormDefinition,
    stored: FormDefinition | undefined,
    others: FormDefinition[],
): DefinitionIssue[] => {
    const issues: DefinitionIssue[] = []
    const error = (target: IssueTarget, id: string, fallback: string, fieldId?: string, vars?: Record<string, string>) =>
        issues.push({ severity: 'error', target, id, fallback, fieldId, vars })
    const warning = (target: IssueTarget, id: string, fallback: string, fieldId?: string, vars?: Record<string, string>) =>
        issues.push({ severity: 'warning', target, id, fallback, fieldId, vars })

    if (draft.name.trim() === '') error('name', 'form-def-error-name', 'Podaj nazwę')
    if (!CODE_PATTERN.test(draft.code)) {
        error('code', 'form-def-error-code-format', 'Kod: wielkie litery, cyfry i _, zaczyna się literą')
    } else if (others.some(d => d.id !== draft.id && d.companyId === draft.companyId && d.code === draft.code)) {
        error('code', 'form-def-error-code-taken', 'Ten kod jest już używany')
    }
    if (!draft.fields.some(f => f.type !== 'heading')) error('fields', 'form-def-error-no-fields', 'Dodaj co najmniej jedno pole')

    const published = publishedOf(stored)
    const keyCounts = new Map<string, number>()
    for (const field of draft.fields) keyCounts.set(field.key, (keyCounts.get(field.key) ?? 0) + 1)

    for (const field of draft.fields) {
        const locks = fieldLocks(stored, field)

        if (!KEY_PATTERN.test(field.key)) {
            error('key', 'form-def-error-key-format', 'Klucz: litery, cyfry i _, zaczyna się literą', field.id)
        } else if ((keyCounts.get(field.key) ?? 0) > 1) {
            error('key', 'form-def-error-key-duplicate', 'Klucz powtarza się w formularzu', field.id)
        } else if (!locks.locked && published.keys.includes(field.key)) {
            error('key', 'form-def-error-key-reserved', 'Klucz był już opublikowany i jest zarezerwowany', field.id)
        }

        checkLabel(field.label, field.id, 'label', error, warning)

        if (hasOptions(field.type)) {
            const options = field.options ?? []
            if (options.length === 0) error('options', 'form-def-error-no-options', 'Dodaj co najmniej jedną opcję', field.id)
            const reserved = published.options[field.key] ?? []
            options.forEach((option, index) => {
                const vars = { value: option.value }
                if (!OPTION_VALUE_PATTERN.test(option.value)) {
                    error('options', 'form-def-error-option-format', `Wartość opcji "${option.value}": litery, cyfry, _ i -`, field.id, vars)
                } else if (options.findIndex(o => o.value === option.value) !== index) {
                    error('options', 'form-def-error-option-duplicate', `Wartość opcji "${option.value}" powtarza się`, field.id, vars)
                } else if (reserved.includes(option.value) && !isOptionLocked(locks, options, index)) {
                    error('options', 'form-def-error-option-reserved', `Wartość opcji "${option.value}" jest zarezerwowana`, field.id, vars)
                }
                checkLabel(option.label, field.id, 'options', error, warning)
            })
        }
    }
    return issues
}

const checkLabel = (
    label: { PL: string, EN: string },
    fieldId: string,
    target: IssueTarget,
    error: (target: IssueTarget, id: string, fallback: string, fieldId?: string) => void,
    warning: (target: IssueTarget, id: string, fallback: string, fieldId?: string, vars?: Record<string, string>) => void,
) => {
    const pl = label.PL.trim() !== ''
    const en = label.EN.trim() !== ''
    if (!pl && !en) error(target, target === 'label' ? 'form-def-error-label' : 'form-def-error-option-label', 'Brak etykiety', fieldId)
    else if (!pl || !en) warning(target, 'form-def-warning-translation', `Brak tłumaczenia ${pl ? 'EN' : 'PL'}`, fieldId, { language: pl ? 'EN' : 'PL' })
}

/** A fresh draft: new ids everywhere, nothing published, a free code. */
export const cloneDefinition = (source: FormDefinition, all: FormDefinition[], nameSuffix: string): FormDefinition => {
    const codes = all.filter(d => d.companyId === source.companyId).map(d => d.code)
    return {
        ...emptyDefinition(),
        companyId: source.companyId,
        name: `${source.name} ${nameSuffix}`,
        code: uniqueKey(`${source.code}_COPY`, codes),
        fields: source.fields.map(f => ({ ...JSON.parse(JSON.stringify(f)), id: crypto.randomUUID() })),
    }
}
